/**
 * Courbe des marges : revente, production propre, et leur somme.
 *
 * Les deux composantes sont EMPILÉES — la hauteur de la barre est donc la marge
 * totale — et la ligne qui les coiffe suit cette somme dans le temps : l'œil lit
 * une tendance bien mieux sur un trait que sur des sommets de barres.
 *
 * ⚠️ La « marge » de la production propre vaut son CA HT, parce que le coût
 * d'achat d'un légume qu'on a cultivé est nul par construction : semences,
 * travail et terre ne sont pas dans le logiciel. C'est une CONTRIBUTION, pas un
 * taux comparable à celui de la revente — le bandeau sous le graphique le dit,
 * sans quoi on lirait « 100 % de marge » et on en tirerait une conclusion fausse.
 */

import { useEffect, useState } from 'react';
import {
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import ChartCard from './ChartCard';
import { api } from '../../services/api';
import { logger } from '../../utils/logger';

type Grain = 'auto' | 'day' | 'week' | 'month';

interface Point {
  bucket: string;
  label: string;
  resale_margin_eur: number;
  resale_margin_percent: number;
  resale_ca_ht_eur: number;
  resale_cost_eur: number;
  resale_uncovered_ca_ht_eur: number;
  own_margin_eur: number;
  own_ca_ht_eur: number;
  total_margin_eur: number;
}

interface Timeline {
  granularity: 'day' | 'week' | 'month';
  points: Point[];
  totals: {
    resale_ca_ht_eur: number;
    resale_margin_eur: number;
    resale_margin_percent: number;
    own_margin_eur: number;
    total_margin_eur: number;
    coverage_percent: number;
  };
}

const eur = (v: number) =>
  new Intl.NumberFormat('fr-BE', { style: 'currency', currency: 'EUR' }).format(v || 0);

const GRAINS: Array<{ key: Grain; label: string }> = [
  { key: 'auto', label: 'Auto' },
  { key: 'day', label: 'Jour' },
  { key: 'week', label: 'Semaine' },
  { key: 'month', label: 'Mois' },
];

export default function CourbeMarges({ from, to }: { from: string; to: string }) {
  const [grain, setGrain] = useState<Grain>('auto');
  const [data, setData] = useState<Timeline | null>(null);
  const [loading, setLoading] = useState(true);
  const [erreur, setErreur] = useState<string | null>(null);

  useEffect(() => {
    let annule = false;
    (async () => {
      setLoading(true);
      setErreur(null);
      try {
        const r = await api.getMarginsTimeline(from, to, grain === 'auto' ? undefined : grain);
        if (!annule) setData(r);
      } catch (e: any) {
        logger.error('[CourbeMarges] chargement échoué:', e);
        if (!annule) setErreur(e?.message || 'Chargement impossible');
      } finally {
        if (!annule) setLoading(false);
      }
    })();
    return () => {
      annule = true;
    };
  }, [from, to, grain]);

  const t = data?.totals;
  const vide = !loading && !erreur && (data?.points.length ?? 0) === 0;

  return (
    <ChartCard
      title="Marges dans le temps"
      subtitle="Revente, production propre, et leur somme"
      loading={loading}
      actions={
        <div className="flex gap-1">
          {GRAINS.map((g) => (
            <button
              key={g.key}
              type="button"
              onClick={() => setGrain(g.key)}
              className={`px-2.5 py-1 text-xs rounded-lg border transition-colors ${
                grain === g.key
                  ? 'bg-[#2d6a4f] text-white border-[#2d6a4f]'
                  : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
              }`}
            >
              {g.label}
            </button>
          ))}
        </div>
      }
    >
      {erreur && <p className="text-sm text-red-700 font-medium mb-3">{erreur}</p>}

      {t && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
          <div className="bg-[#eef5f0] rounded-lg p-3">
            <p className="text-xs text-gray-600">Marge revente</p>
            <p className="text-lg font-bold text-[#2d6a4f]">{eur(t.resale_margin_eur)}</p>
            <p className="text-xs text-gray-500">{t.resale_margin_percent} % du CA HT</p>
          </div>
          <div className="bg-[#fdf4e3] rounded-lg p-3">
            <p className="text-xs text-gray-600">Production propre</p>
            <p className="text-lg font-bold text-[#b45f2e]">{eur(t.own_margin_eur)}</p>
            <p className="text-xs text-gray-500">sans coût de culture</p>
          </div>
          <div className="bg-gray-100 rounded-lg p-3">
            <p className="text-xs text-gray-600">Total</p>
            <p className="text-lg font-bold text-gray-900">{eur(t.total_margin_eur)}</p>
          </div>
          <div className="bg-gray-100 rounded-lg p-3">
            <p className="text-xs text-gray-600">Couverture</p>
            <p className="text-lg font-bold text-gray-900">{t.coverage_percent} %</p>
            <p className="text-xs text-gray-500">du CA revente a un coût connu</p>
          </div>
        </div>
      )}

      {vide ? (
        <p className="text-sm text-gray-500 py-10 text-center">
          Aucune vente sur cette période.
        </p>
      ) : (
        <ResponsiveContainer width="100%" height={320}>
          <ComposedChart data={data?.points || []}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="label" fontSize={11} interval="preserveStartEnd" />
            <YAxis tickFormatter={(v) => `${Number(v).toFixed(0)} €`} fontSize={11} />
            <Tooltip
              formatter={(v: any, nom: any) => [eur(Number(v)), nom]}
              labelFormatter={(l) => String(l)}
            />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            <Bar
              dataKey="resale_margin_eur"
              name="Marge revente"
              stackId="marge"
              fill="#2d6a4f"
              radius={[0, 0, 0, 0]}
            />
            <Bar
              dataKey="own_margin_eur"
              name="Production propre"
              stackId="marge"
              fill="#d99b4e"
              radius={[4, 4, 0, 0]}
            />
            <Line
              type="monotone"
              dataKey="total_margin_eur"
              name="Marge totale"
              stroke="#1f2937"
              strokeWidth={2}
              dot={false}
            />
          </ComposedChart>
        </ResponsiveContainer>
      )}

      <p className="text-xs text-gray-500 mt-3 leading-relaxed">
        La production propre est comptée à <strong>coût d'achat nul</strong> — on ne s'achète pas
        ses propres légumes. Sa contribution est donc son chiffre d'affaires HT, et elle ne se
        compare pas à un taux de marge de revente : semences, travail et terre ne sont pas dans
        le logiciel. Les articles de revente dont le prix d'achat est inconnu sont{' '}
        <strong>exclus</strong> du calcul plutôt que comptés à coût nul — c'est ce que mesure la
        couverture.
      </p>
    </ChartCard>
  );
}
